import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { LogIn, UserPlus, Clock, MapPin, Users, Sun, Sprout } from 'lucide-react';

export default function LoginPage() {
  const brandLogo = '/omlogo.png';
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    role: 'employee',
  });
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.username, formData.password);
        toast.success('Welcome back!');
      } else {
        await register({
          username: formData.username,
          password: formData.password,
          email: formData.email,
          role: formData.role,
        });
        toast.success('Account created successfully!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Clock, title: 'Smart Attendance', desc: 'Easy check-in with location tracking' },
    { icon: MapPin, title: 'Farm Locations', desc: 'Multi-location workforce management' },
    { icon: Users, title: 'Team Management', desc: 'Complete employee lifecycle' },
    { icon: Sprout, title: 'Organic Growth', desc: 'Analytics for sustainable teams' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#eef7f2] via-white to-[#f4efe6] flex">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 nature-dots" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1920&q=80)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#022b1b]/95 via-[#033923]/90 to-[#876a2d]/70" />

        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur border border-white/15 flex items-center justify-center p-2">
                <img src={brandLogo} alt="Organic Mandya" className="h-full w-full object-contain" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Organic Mandya</h1>
                <p className="text-sm text-white/80 uppercase tracking-[0.24em]">HRMS Portal</p>
              </div>
            </div>

            <h2 className="text-4xl font-bold mb-4 leading-tight">
              Nurturing Our
              <br />
              <span className="text-[#f6d77a]">People & Purpose</span>
            </h2>
            <p className="text-lg text-white/90 max-w-md">
              Empowering our organic farming community with modern HR solutions. Track, grow, and thrive together.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-colors"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <feature.icon className="w-8 h-8 text-[#f6d77a] mb-3" />
                <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                <p className="text-sm text-white/80">{feature.desc}</p>
              </div>
            ))}
          </div>

          <div className="absolute bottom-8 left-12 flex items-center gap-2 text-white/60 text-sm">
            <Sun className="w-4 h-4" />
            <span>Growing naturally since 2014</span>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md fade-in">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-14 h-14 rounded-2xl bg-[#033923] flex items-center justify-center p-2 shadow-lg shadow-[#033923]/20">
              <img src={brandLogo} alt="Organic Mandya" className="h-full w-full object-contain" />
            </div>
            <div>
              <span className="text-xl font-bold text-[#033923]">Organic Mandya</span>
              <p className="text-xs uppercase tracking-[0.2em] text-[#6b7f76]">HRMS Portal</p>
            </div>
          </div>

          <Card className="border-[#d7e7de] shadow-xl shadow-[#033923]/8">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-[#033923]">
                {isLogin ? 'Welcome Back' : 'Join Our Team'}
              </CardTitle>
              <CardDescription className="text-[#5d746a]">
                {isLogin
                  ? 'Sign in to access your dashboard'
                  : 'Create your account to get started'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-[#033923]">Username</Label>
                  <Input
                    id="username"
                    data-testid="login-username"
                    placeholder="Enter your username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="border-[#d1dfd7] focus:border-[#033923] focus:ring-[#033923]"
                    required
                  />
                </div>

                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#033923]">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="register-email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="border-[#d1dfd7] focus:border-[#033923] focus:ring-[#033923]"
                      required={!isLogin}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#033923]">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    data-testid="login-password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="border-[#d1dfd7] focus:border-[#033923] focus:ring-[#033923]"
                    required
                  />
                </div>

                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="role" className="text-[#033923]">Role</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value) => setFormData({ ...formData, role: value })}
                    >
                      <SelectTrigger data-testid="register-role" className="border-[#d1dfd7]">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="hr">HR</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-[#033923] hover:bg-[#022b1b] text-white glow-primary"
                  data-testid="login-submit"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner mr-2" />
                  ) : isLogin ? (
                    <LogIn className="w-4 h-4 mr-2" />
                  ) : (
                    <UserPlus className="w-4 h-4 mr-2" />
                  )}
                  {isLogin ? 'Sign In' : 'Create Account'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-sm text-[#33584b] hover:text-[#033923] transition-colors"
                  data-testid="toggle-auth-mode"
                >
                  {isLogin
                    ? "Don't have an account? Sign up"
                    : 'Already have an account? Sign in'}
                </button>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-[#5d746a] mt-6">
            Organic Mandya {new Date().getFullYear()} - Pure. Natural. Honest.
          </p>
        </div>
      </div>
    </div>
  );
}
